import { Link, useParams } from 'react-router-dom';
import { useMemo } from 'react';
import { mangas, categories } from '@/mocks/manga';

export default function MangaDetail() {
  const { id } = useParams<{ id: string }>();

  const manga = useMemo(() => mangas.find((m) => m.id === id), [id]);
  const relatedMangas = useMemo(() => {
    if (!manga) return [];
    return mangas.filter((m) => m.category === manga.category && m.id !== manga.id).slice(0, 4);
  }, [manga]);

  if (!manga) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您要查找的漫画不存在或已被下架。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryObj = categories.find((c) => c.slug === manga.category);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.yinghuadongman.com/" },
              { "@type": "ListItem", "position": 2, "name": `${categoryObj?.name || ''}漫画`, "item": `https://www.yinghuadongman.com/category/${manga.category}` },
              { "@type": "ListItem", "position": 3, "name": manga.title, "item": `https://www.yinghuadongman.com/manga/${manga.id}` }
            ]
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `${manga.title} - 韩漫下拉式在线阅读`,
            "author": { "@type": "Person", "name": manga.author },
            "datePublished": "2025-01-01",
            "dateModified": manga.updateDate,
            "description": manga.description,
            "image": manga.coverImage,
            "publisher": { "@type": "Organization", "name": "樱花动漫网" }
          })
        }}
      />

      {/* Breadcrumb */}
      <nav className="pt-20 pb-0 px-4 md:px-6" aria-label="面包屑导航">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-foreground-400 flex-wrap">
          <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <Link to={`/category/${manga.category}`} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">{categoryObj?.name || manga.category}漫画</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-600 truncate">{manga.title}</span>
        </div>
      </nav>

      {/* Manga Header */}
      <section className="py-6 md:py-10 px-4 md:px-6" aria-labelledby="manga-title">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-6 md:gap-10">
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                <img
                  src={manga.coverImage}
                  alt={`${manga.title} 韩漫封面`}
                  title={`${manga.title} - 免费韩漫下拉式阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 id="manga-title" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{manga.title}</h1>
              <p className="mt-1 text-sm text-foreground-400">{manga.altTitle}</p>

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <span className="px-3 py-1 text-xs font-medium bg-primary-500/10 text-primary-600 rounded-full whitespace-nowrap">
                  {manga.rating} 分
                </span>
                <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                  manga.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {manga.status}
                </span>
                {manga.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-500 rounded-full whitespace-nowrap">{tag}</span>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 py-5 border-y border-background-200/70">
                <div>
                  <div className="text-xs text-foreground-400">作者</div>
                  <div className="text-sm font-medium text-foreground-800 mt-0.5">{manga.author}</div>
                </div>
                <div>
                  <div className="text-xs text-foreground-400">画师</div>
                  <div className="text-sm font-medium text-foreground-800 mt-0.5">{manga.artist}</div>
                </div>
                <div>
                  <div className="text-xs text-foreground-400">阅读量</div>
                  <div className="text-sm font-medium text-foreground-800 mt-0.5">{manga.views.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs text-foreground-400">收藏数</div>
                  <div className="text-sm font-medium text-foreground-800 mt-0.5">{manga.favorites.toLocaleString()}</div>
                </div>
              </div>

              <p className="mt-5 text-sm text-foreground-600 leading-relaxed">{manga.description}</p>

              <div className="flex flex-wrap gap-3 mt-6">
                <button className="px-6 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-line mr-1.5"></i>开始阅读
                </button>
                <button className="px-6 py-2.5 text-sm font-medium text-foreground-600 border border-background-200/70 hover:bg-background-100 rounded-lg transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line mr-1.5"></i>加入收藏
                </button>
                <button className="px-6 py-2.5 text-sm font-medium text-foreground-600 border border-background-200/70 hover:bg-background-100 rounded-lg transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-share-forward-line mr-1.5"></i>分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reading Area */}
      <section className="py-6 md:py-10 px-4 md:px-6 bg-background-100/50" aria-labelledby="reading-heading">
        <div className="max-w-3xl mx-auto">
          <h2 id="reading-heading" className="text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
            下拉式阅读 · {manga.title}
          </h2>

          <div className="space-y-4">
            {/* Chapter 1 */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-foreground-900">第1话 · 一切的开始</span>
                  <span className="ml-2 text-xs text-foreground-400">2026-01-15</span>
                </div>
                <span className="text-xs text-primary-500 font-medium whitespace-nowrap">免费</span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 gap-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20vertical%20scroll%20comic%20panel%20page%2C%20dramatic%20storytelling%20scene%20with%20detailed%20character%20expressions%2C%20warm%20atmospheric%20lighting%2C%20professional%20digital%20manhwa%20art%20style%2C%20clean%20panel%20layout%2C%20cinematic%20angles%2C%20no%20speech%20bubbles&width=800&height=1200&seq=reading-manga-1-ch1-p1&orientation=portrait"
                    alt={`${manga.title} 第1话 第1页`}
                    title={`${manga.title} 下拉式阅读 第1话`}
                    className="w-full rounded-lg"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20vertical%20scroll%20comic%20panel%20page%2C%20emotional%20character%20interaction%20scene%2C%20soft%20romantic%20atmosphere%2C%20detailed%20background%20art%2C%20digital%20manhwa%20style%2C%20flowing%20composition%2C%20no%20text&width=800&height=1200&seq=reading-manga-1-ch1-p2&orientation=portrait"
                    alt={`${manga.title} 第1话 第2页`}
                    className="w-full rounded-lg"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20vertical%20scroll%20comic%20panel%20page%2C%20tense%20confrontation%20scene%20with%20dynamic%20action%2C%20dramatic%20shadows%20and%20highlights%2C%20digital%20manhwa%20art%20style%2C%20cinematic%20framing%2C%20no%20speech%20bubbles&width=800&height=1200&seq=reading-manga-1-ch1-p3&orientation=portrait"
                    alt={`${manga.title} 第1话 第3页`}
                    className="w-full rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Chapter 2 */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-foreground-900">第2话 · 命运的相遇</span>
                  <span className="ml-2 text-xs text-foreground-400">2026-01-22</span>
                </div>
                <span className="text-xs text-primary-500 font-medium whitespace-nowrap">免费</span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 gap-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20vertical%20scroll%20comic%20panel%20page%2C%20chance%20encounter%20scene%20in%20urban%20setting%2C%20warm%20afternoon%20lighting%2C%20detailed%20character%20expressions%2C%20digital%20manhwa%20art%20style%2C%20no%20text&width=800&height=1200&seq=reading-manga-1-ch2-p1&orientation=portrait"
                    alt={`${manga.title} 第2话 第1页`}
                    className="w-full rounded-lg"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20vertical%20scroll%20comic%20panel%20page%2C%20intimate%20conversation%20scene%20in%20cafe%2C%20soft%20warm%20interior%20lighting%2C%20detailed%20facial%20expressions%2C%20digital%20manhwa%20art%20style%2C%20no%20speech%20bubbles&width=800&height=1200&seq=reading-manga-1-ch2-p2&orientation=portrait"
                    alt={`${manga.title} 第2话 第2页`}
                    className="w-full rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Chapter 3 */}
            <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
              <div className="px-5 py-4 border-b border-background-200/70 flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-foreground-900">第3话 · 悄然心动</span>
                  <span className="ml-2 text-xs text-foreground-400">2026-02-05</span>
                </div>
                <span className="text-xs text-primary-500 font-medium whitespace-nowrap">免费</span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 gap-4">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20vertical%20scroll%20comic%20panel%20page%2C%20romantic%20moment%20under%20cherry%20blossoms%2C%20soft%20pink%20and%20golden%20lighting%2C%20tender%20atmosphere%2C%20digital%20manhwa%20art%20style%2C%20beautiful%20composition%2C%20no%20text&width=800&height=1200&seq=reading-manga-1-ch3-p1&orientation=portrait"
                    alt={`${manga.title} 第3话 第1页`}
                    className="w-full rounded-lg"
                  />
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20vertical%20scroll%20comic%20panel%20page%2C%20emotional%20closeup%20of%20characters%20sharing%20a%20quiet%20moment%2C%20warm%20evening%20light%2C%20delicate%20expressions%2C%20digital%20manhwa%20art%20style%2C%20no%20speech%20bubbles&width=800&height=1200&seq=reading-manga-1-ch3-p2&orientation=portrait"
                    alt={`${manga.title} 第3话 第2页`}
                    className="w-full rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Chapter Navigator */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <button className="px-5 py-2.5 text-sm font-medium text-foreground-500 border border-background-200/70 rounded-lg hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap" disabled>
              <i className="ri-arrow-left-line mr-1"></i>上一话
            </button>
            <span className="text-sm text-foreground-500">第 1-3 话 / 共 3 话</span>
            <button className="px-5 py-2.5 text-sm font-medium text-foreground-500 border border-background-200/70 rounded-lg hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap" disabled>
              下一话<i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Related Manga */}
      {relatedMangas.length > 0 && (
        <section className="py-10 md:py-14 px-4 md:px-6" aria-labelledby="related-heading">
          <div className="max-w-7xl mx-auto">
            <h2 id="related-heading" className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {relatedMangas.map((rm) => (
                <Link
                  key={rm.id}
                  to={`/manga/${rm.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-2.5">
                    <img
                      src={rm.coverImage}
                      alt={rm.title}
                      title={`${rm.title} - 韩漫推荐`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded-md">{rm.rating}</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{rm.title}</h3>
                  <p className="text-xs text-foreground-400 mt-0.5">{rm.views.toLocaleString()} 阅读</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}